import { formatCop, vehicleRowSummary } from "../../constants/automapInventoryImportConstants";
import { useDealerPortalIntl } from "../../hooks/useDealerPortalIntl";
import { useLocale } from "../../intl";

export function ImportStepIndicator({ currentStep }) {
  const { importStepOptions } = useDealerPortalIntl();
  const stepIndex = importStepOptions.findIndex((step) => step.key === currentStep);
  return (
    <ol className="import-steps">
      {importStepOptions.map((step, index) => (
        <li
          key={step.key}
          className={index < stepIndex ? "done" : index === stepIndex ? "active" : ""}
        >
          <span>{index + 1}</span>
          {step.label}
        </li>
      ))}
    </ol>
  );
}

export function ImportSummaryCards({ importRecord }) {
  const { t } = useDealerPortalIntl();
  if (!importRecord) return null;
  return (
    <div className="import-summary-grid">
      <div className="import-summary-card">
        <strong>{importRecord.totalRows}</strong>
        <span>{t("import.summaryTotalRows")}</span>
      </div>
      <div className="import-summary-card ok">
        <strong>{importRecord.validRows}</strong>
        <span>{t("import.summaryValid")}</span>
      </div>
      <div className="import-summary-card warn">
        <strong>{importRecord.warningRows}</strong>
        <span>{t("import.summaryWarnings")}</span>
      </div>
      <div className="import-summary-card err">
        <strong>{importRecord.errorRows}</strong>
        <span>{t("import.summaryErrors")}</span>
      </div>
      <div className="import-summary-card">
        <strong>{importRecord.duplicateRows}</strong>
        <span>{t("import.summaryDuplicates")}</span>
      </div>
      <div className="import-summary-card">
        <strong>{importRecord.createdCount ?? 0}</strong>
        <span>{t("import.summaryCreated")}</span>
      </div>
      <div className="import-summary-card">
        <strong>{importRecord.updatedCount ?? 0}</strong>
        <span>{t("import.summaryUpdated")}</span>
      </div>
      <div className="import-summary-card">
        <strong>{importRecord.skippedCount ?? 0}</strong>
        <span>{t("import.summarySkipped")}</span>
      </div>
    </div>
  );
}

export function ImportPreviewTable({ rows, onActionChange, editable = true }) {
  const { t, formatDuplicateStatus, rowActionOptions } = useDealerPortalIntl();
  const { locale } = useLocale();

  if (!rows?.length) {
    return <p className="dealer-state-box">{t("import.previewEmpty")}</p>;
  }

  return (
    <div className="import-table-wrap">
      <table className="dealer-inventory-table import-preview-table">
        <thead>
          <tr>
            <th>{t("import.previewColNumber")}</th>
            <th>{t("import.previewColVehicle")}</th>
            <th>{t("import.previewColPrice")}</th>
            <th>{t("import.previewColMileage")}</th>
            <th>{t("import.previewColCity")}</th>
            <th>{t("import.previewColStatus")}</th>
            <th>{t("import.previewColWarnings")}</th>
            <th>{t("import.previewColErrors")}</th>
            <th>{t("import.previewColDuplicate")}</th>
            <th>{t("import.previewColAction")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className={`import-row-${row.status}`}>
              <td>{row.rowNumber}</td>
              <td>{vehicleRowSummary(row)}</td>
              <td>{formatCop(row.normalizedData?.priceCop, locale)}</td>
              <td>{row.normalizedData?.mileageKm?.toLocaleString(locale) ?? "—"}</td>
              <td>{row.normalizedData?.city || "—"}</td>
              <td>
                <span className={`import-badge import-badge--${row.status}`}>{row.status}</span>
              </td>
              <td className="import-cell-warn">{(row.validationWarnings || []).join("; ") || "—"}</td>
              <td className="import-cell-err">{(row.validationErrors || []).join("; ") || "—"}</td>
              <td>{formatDuplicateStatus(row.duplicateStatus)}</td>
              <td>
                {editable ? (
                  <select
                    value={row.action}
                    onChange={(e) => onActionChange?.(row.id, e.target.value)}
                  >
                    {rowActionOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  row.action
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ImportStatusBadge({ status }) {
  const { formatImportStatus } = useDealerPortalIntl();
  return (
    <span className={`import-badge import-badge--${status}`}>{formatImportStatus(status)}</span>
  );
}

export function FieldMappingForm({ fields, headers, mapping, onChange, sampleRow }) {
  const { t } = useDealerPortalIntl();
  return (
    <div className="import-mapping-grid">
      {fields.map((field) => {
        const source = mapping[field.key] || "";
        const sample = source && sampleRow ? sampleRow[source] : "";
        return (
          <label key={field.key} className="import-mapping-row">
            <span>
              {field.label}
              {field.required
                ? t("import.fieldRequired")
                : field.recommended
                  ? t("import.fieldRecommended")
                  : ""}
            </span>
            <select value={source} onChange={(e) => onChange(field.key, e.target.value)}>
              <option value="">{t("import.unmapped")}</option>
              {headers.map((header) => (
                <option key={header} value={header}>
                  {header}
                </option>
              ))}
            </select>
            <small className="muted">
              {sample
                ? `${t("import.samplePrefix")} ${String(sample).slice(0, 60)}`
                : t("import.noSample")}
            </small>
          </label>
        );
      })}
    </div>
  );
}
